import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import AssignmentIndOutlinedIcon from '@mui/icons-material/AssignmentIndOutlined'
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined'
import AddOutlinedIcon from '@mui/icons-material/AddOutlined'
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined'
import { Skeleton } from '@mui/material'
import Modal from 'components/atoms/Modal'
import UserPicker from 'features/taskManagement/components/UserPicker'
import PrimaryButton from 'components/atoms/PrimaryButton'
import AttachTagsToUploadingFile from 'features/bpDetails/Components/files/AttachTagsToUploadingFiles'
import { assignChecklistItem } from 'services/taskService'
import { fetchBpUnits } from 'services/unitsService'
import { fetchBp } from 'services/BpService'
import { getFileTagsForBpRoutine } from 'ducks/files/actions'
import { selectFileTagsForBp } from 'ducks/files/selectors'
import { getApiErrors } from 'utils/errors'

const normalizeId = value => (value ? String(value) : '')

const getRuleSourceName = item => {
  const rawName =
    item?.file_type?.name || item?.fileType?.name || item?.title || ''
  const withoutCount = rawName.replace(/\s*\(\d+\s+files?\)\s*$/i, '').trim()
  const segments = withoutCount
    .split('/')
    .map(segment => segment.trim())
    .filter(Boolean)

  return segments.length > 0
    ? segments[segments.length - 1]
    : withoutCount || 'File'
}

const buildGeneratedRuleName = (item, index) =>
  `Upload files - ${getRuleSourceName(item)} - rule ${index + 1}`

const buildEmptyRule = units => ({
  key: `rule-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  ruleName: '',
  tagIds: [],
  assignmentScope: 'bp',
  assigneeUserIds: [],
  unitAssignments: (units || []).map(unit => ({
    buildingUnitPassportId: normalizeId(unit.id),
    name: unit.name,
    assigneeUserIds: []
  }))
})

const groupTagRequirements = item => {
  const requirements =
    item?.bp_tag_requirements ||
    item?.bpTagRequirements ||
    item?.tag_requirements ||
    item?.tagRequirements ||
    []

  return requirements.reduce((acc, requirement) => {
    const groupNo = Number(requirement?.group_no ?? requirement?.groupNo ?? 0)
    const tagId = normalizeId(
      requirement?.file_tag_id || requirement?.fileTagId
    )
    if (!acc[groupNo]) acc[groupNo] = []
    if (tagId && !acc[groupNo].includes(tagId)) {
      acc[groupNo].push(tagId)
    }
    return acc
  }, {})
}

const buildRulesFromItem = (item, units) => {
  const groupedTags = groupTagRequirements(item)
  const assignments = item?.assignments || []
  const assignmentsByGroup = assignments.reduce((acc, assignment) => {
    const groupNo = Number(assignment?.group_no ?? 0)
    if (!acc[groupNo]) acc[groupNo] = []
    acc[groupNo].push(assignment)
    return acc
  }, {})
  const groupNos = Array.from(
    new Set([
      ...Object.keys(groupedTags).map(Number),
      ...Object.keys(assignmentsByGroup).map(Number)
    ])
  ).sort((a, b) => a - b)

  const rules = groupNos.map(groupNo => {
    const groupAssignments = assignmentsByGroup[groupNo] || []
    const ruleName =
      groupAssignments.find(entry => entry?.rule_name || entry?.ruleName)
        ?.rule_name ||
      groupAssignments.find(entry => entry?.rule_name || entry?.ruleName)
        ?.ruleName ||
      ''
    const unitAssignments = groupAssignments.filter(
      entry => (entry?.assignment_scope || entry?.assignmentScope) === 'unit'
    )
    const bpAssignment = groupAssignments.find(
      entry =>
        (entry?.assignment_scope || entry?.assignmentScope || 'bp') === 'bp'
    )

    if (unitAssignments.length > 0) {
      const byUnitId = unitAssignments.reduce((acc, entry) => {
        const unitId = normalizeId(
          entry?.building_unit_passport_id ||
            entry?.buildingUnitPassportId ||
            entry?.unit?.id
        )
        if (!unitId) return acc
        acc[unitId] = {
          buildingUnitPassportId: unitId,
          name: entry?.unit?.name || '',
          assigneeUserIds: []
        }
        return acc
      }, {})

      unitAssignments.forEach(entry => {
        const unitId = normalizeId(
          entry?.building_unit_passport_id ||
            entry?.buildingUnitPassportId ||
            entry?.unit?.id
        )
        const assigneeIds = [
          ...(entry?.assignee_user_ids || []),
          entry?.assignee_user_id || entry?.assigneeUserId || entry?.assignee?.id
        ]
          .filter(Boolean)
          .map(normalizeId)

        if (!unitId || !byUnitId[unitId]) return
        byUnitId[unitId].assigneeUserIds = Array.from(
          new Set([...(byUnitId[unitId].assigneeUserIds || []), ...assigneeIds])
        )
      })

      return {
        key: `rule-${groupNo}`,
        ruleName,
        tagIds: groupedTags[groupNo] || [],
        assignmentScope: 'unit',
        assigneeUserIds: [],
        unitAssignments: (units || []).map(unit => ({
          buildingUnitPassportId: normalizeId(unit.id),
          name: unit.name,
          assigneeUserIds: byUnitId[normalizeId(unit.id)]?.assigneeUserIds || []
        }))
      }
    }

    return {
      key: `rule-${groupNo}`,
      ruleName,
      tagIds: groupedTags[groupNo] || [],
      assignmentScope: 'bp',
      assigneeUserIds: Array.from(
        new Set(
          (groupAssignments || [])
            .filter(
              entry =>
                (entry?.assignment_scope || entry?.assignmentScope || 'bp') === 'bp'
            )
            .flatMap(entry => [
              ...(entry?.assignee_user_ids || []),
              entry?.assignee_user_id || entry?.assigneeUserId || entry?.assignee?.id
            ])
            .filter(Boolean)
            .map(normalizeId)
        )
      ),
      unitAssignments: (units || []).map(unit => ({
        buildingUnitPassportId: normalizeId(unit.id),
        name: unit.name,
        assigneeUserIds: []
      }))
    }
  })

  return rules
}

const AssignChecklistItemModal = ({
  bpId,
  templateId,
  item,
  itemIdOverride,
  onAssigned,
  children
}) => {
  const dispatch = useDispatch()
  const tags = useSelector(selectFileTagsForBp)
  const [open, setOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveMode, setSaveMode] = useState('save')
  const [rules, setRules] = useState([])
  const [units, setUnits] = useState([])
  const [loadingUnits, setLoadingUnits] = useState(false)
  const [hasTaskManagement, setHasTaskManagement] = useState(false)
  const [bpName, setBpName] = useState('')
  const [bulkModal, setBulkModal] = useState({
    open: false,
    ruleKey: null,
    search: '',
    selectedUnitIds: [],
    assigneeUserIds: []
  })
  const isInitialLoading = open && loadingUnits && rules.length === 0

  const knownUsers = useMemo(() => {
    const users = []
    const pushUser = user => {
      if (
        !user?.id ||
        users.some(entry => normalizeId(entry.id) === normalizeId(user.id))
      )
        return
      users.push(user)
    }

    pushUser(item?.assignment?.assignee)
    ;(item?.assignment?.assignees || []).forEach(pushUser)
    ;(item?.assignments || []).forEach(assignment =>
      {
        pushUser(assignment?.assignee)
        ;(assignment?.assignees || []).forEach(pushUser)
      }
    )

    return users
  }, [item])

  const rulesWithAssignments = useMemo(
    () =>
      rules.filter(rule =>
        rule.assignmentScope === 'bp'
          ? rule.assigneeUserIds.length > 0
          : rule.unitAssignments.some(unitAssignment =>
              unitAssignment.assigneeUserIds.length > 0
            )
      ).length,
    [rules]
  )

  const loadUnits = async () => {
    if (!bpId) return

    setLoadingUnits(true)
    try {
      const { data } = await fetchBpUnits({ bpId })
      const nextUnits = Array.isArray(data?.data) ? data.data : []
      setUnits(nextUnits)
      setRules(buildRulesFromItem(item, nextUnits))
    } catch (error) {
      setUnits([])
      setRules(buildRulesFromItem(item, []))
      toast.error(getApiErrors(error) || 'Failed to load units')
    } finally {
      setLoadingUnits(false)
    }
  }

  const loadBpTaskManagement = async () => {
    if (!bpId) return

    try {
      const { data } = await fetchBp(bpId)
      const nextBp = data?.data || {}
      setBpName(nextBp?.name || '')
      setHasTaskManagement(
        Boolean(
          nextBp?.hasTaskManagement ??
            nextBp?.has_task_management ??
            nextBp?.taskManagementEnabled ??
            nextBp?.task_management_enabled
        )
      )
    } catch (error) {
      setBpName('')
      setHasTaskManagement(false)
    }
  }

  useEffect(() => {
    if (!open) return

    dispatch(getFileTagsForBpRoutine({ bpId }))
    loadUnits()
    loadBpTaskManagement()
  }, [bpId, dispatch, item, open, templateId])

  const closeModal = () => {
    setOpen(false)
    setSaveMode('save')
  }

  const updateRule = (ruleKey, updater) => {
    setRules(prev =>
      prev.map(rule =>
        rule.key === ruleKey
          ? typeof updater === 'function'
            ? updater(rule)
            : { ...rule, ...updater }
          : rule
      )
    )
  }

  const handleAddRule = () => {
    setRules(prev => [...prev, buildEmptyRule(units)])
  }

  const handleRemoveRule = ruleKey => {
    setRules(prev => prev.filter(rule => rule.key !== ruleKey))
  }

  const handleScopeChange = (ruleKey, assignmentScope) => {
    updateRule(ruleKey, rule => ({
      ...rule,
      assignmentScope,
      assigneeUserIds: assignmentScope === 'bp' ? rule.assigneeUserIds : [],
      unitAssignments: (units || []).map(unit => {
        const current = rule.unitAssignments.find(
          entry =>
            normalizeId(entry.buildingUnitPassportId) === normalizeId(unit.id)
        )

        return {
          buildingUnitPassportId: normalizeId(unit.id),
          name: unit.name,
          assigneeUserIds:
            assignmentScope === 'unit' ? current?.assigneeUserIds || [] : []
        }
      })
    }))
  }

  const currentBulkRule = useMemo(
    () => rules.find(rule => rule.key === bulkModal.ruleKey) || null,
    [bulkModal.ruleKey, rules]
  )

  const filteredBulkUnitAssignments = useMemo(() => {
    if (!currentBulkRule) return []

    const normalizedSearch = bulkModal.search.trim().toLowerCase()

    return normalizedSearch.length === 0
      ? currentBulkRule.unitAssignments
      : currentBulkRule.unitAssignments.filter(unitAssignment =>
          (unitAssignment.name || '').toLowerCase().includes(normalizedSearch)
        )
  }, [bulkModal.search, currentBulkRule])

  const openBulkAssignModal = rule => {
    setBulkModal({
      open: true,
      ruleKey: rule.key,
      search: '',
      selectedUnitIds: [],
      assigneeUserIds: []
    })
  }

  const closeBulkAssignModal = () => {
    setBulkModal({
      open: false,
      ruleKey: null,
      search: '',
      selectedUnitIds: [],
      assigneeUserIds: []
    })
  }

  const toggleBulkUnitSelection = unitId => {
    setBulkModal(prev => ({
      ...prev,
      selectedUnitIds: prev.selectedUnitIds.includes(unitId)
        ? prev.selectedUnitIds.filter(id => id !== unitId)
        : [...prev.selectedUnitIds, unitId]
    }))
  }

  const handleSelectAllVisibleBulkUnits = () => {
    setBulkModal(prev => ({
      ...prev,
      selectedUnitIds: Array.from(
        new Set([
          ...prev.selectedUnitIds,
          ...filteredBulkUnitAssignments.map(
            unit => unit.buildingUnitPassportId
          )
        ])
      )
    }))
  }

  const handleClearBulkSelection = () => {
    setBulkModal(prev => ({
      ...prev,
      selectedUnitIds: []
    }))
  }

  const handleApplyBulkSelection = () => {
    if (!bulkModal.ruleKey || bulkModal.assigneeUserIds.length === 0) return

    const selectedUnitIds = new Set(bulkModal.selectedUnitIds)
    if (selectedUnitIds.size === 0) return

    updateRule(bulkModal.ruleKey, currentRule => ({
      ...currentRule,
      unitAssignments: currentRule.unitAssignments.map(entry =>
        selectedUnitIds.has(entry.buildingUnitPassportId)
          ? { ...entry, assigneeUserIds: bulkModal.assigneeUserIds }
          : entry
      )
    }))

    closeBulkAssignModal()
  }

  const handleClearBulkAssignedUnits = () => {
    if (!bulkModal.ruleKey) return

    const selectedUnitIds = new Set(bulkModal.selectedUnitIds)
    if (selectedUnitIds.size === 0) return

    updateRule(bulkModal.ruleKey, currentRule => ({
      ...currentRule,
      unitAssignments: currentRule.unitAssignments.map(entry =>
        selectedUnitIds.has(entry.buildingUnitPassportId)
          ? { ...entry, assigneeUserIds: [] }
          : entry
      )
    }))

    closeBulkAssignModal()
  }

  const resolvedItemId = item?.id || itemIdOverride

  const handleSave = async createTasks => {
    if (!bpId || !templateId || !resolvedItemId) return

    setSaving(true)
    setSaveMode(createTasks ? 'create' : 'save')
    try {
      const payload = {
        bpId,
        templateId,
        itemId: resolvedItemId,
        createTasks,
        rules: rules.map(rule => ({
          rule_name:
            (rule.ruleName || '').trim() ||
            buildGeneratedRuleName(
              item,
              rules.findIndex(entry => entry.key === rule.key)
            ),
          tag_ids: rule.tagIds,
          assignment_scope: rule.assignmentScope,
          assignee_user_ids:
            rule.assignmentScope === 'bp' ? rule.assigneeUserIds : [],
          unit_assignments:
            rule.assignmentScope === 'unit'
              ? rule.unitAssignments.map(unitAssignment => ({
                  building_unit_passport_id:
                    unitAssignment.buildingUnitPassportId,
                  assignee_user_ids: unitAssignment.assigneeUserIds || []
                }))
              : []
        }))
      }

      const { data } = await assignChecklistItem(payload)

      onAssigned?.(data?.data)
      setRules(buildRulesFromItem(data?.data || item, units))
      closeBulkAssignModal()
      closeModal()

      toast.success(
        createTasks ? 'Rules saved and tasks created' : 'Upload rules updated'
      )
    } catch (error) {
      toast.error(getApiErrors(error) || 'Failed to update upload rules')
    } finally {
      setSaving(false)
      setSaveMode('save')
    }
  }

  return (
    <>
      <TriggerWrapper
        type='button'
        onClick={e => {
          e.stopPropagation()
          setOpen(true)
        }}
      >
        {children || <TriggerLabel>Configure</TriggerLabel>}
      </TriggerWrapper>
      <Modal
        open={open}
        title='Set up upload rules'
        onClose={closeModal}
        minWidthDesktop='860px'
        maxWidthDesktop='980px'
      >
        <Content>
          <ItemCard>
            <ItemIconWrap>
              <AssignmentIndOutlinedIcon fontSize='inherit' />
            </ItemIconWrap>
            <div>
              <ItemTitle>{item?.title || 'Checklist item'}</ItemTitle>
              {item?.description && (
                <ItemDescription>{item.description}</ItemDescription>
              )}
            </div>
          </ItemCard>

          <RulesSection>
            {isInitialLoading ? (
              <>
                <RulesHeader>
                  <div style={{ flex: 1 }}>
                    <Skeleton variant='text' width='28%' height={34} />
                    <Skeleton variant='text' width='74%' height={24} />
                    <Skeleton variant='text' width='62%' height={22} />
                  </div>
                  <Skeleton variant='rounded' width={180} height={52} />
                </RulesHeader>

                <RulesSummary>
                  <Skeleton variant='rounded' width={120} height={42} />
                  <Skeleton variant='rounded' width={160} height={42} />
                  <Skeleton variant='rounded' width={150} height={42} />
                </RulesSummary>

                <RuleCard>
                  <RuleHeader>
                    <RuleTitleGroup>
                      <Skeleton variant='circular' width={36} height={36} />
                      <RuleTitleContent>
                        <Skeleton variant='text' width={320} height={30} />
                        <Skeleton variant='text' width={160} height={22} />
                      </RuleTitleContent>
                    </RuleTitleGroup>
                    <RuleActions>
                      <Skeleton variant='rounded' width={180} height={48} />
                      <Skeleton variant='rounded' width={150} height={48} />
                    </RuleActions>
                  </RuleHeader>

                  <Skeleton variant='text' width={220} height={28} />

                  <ScopeGrid>
                    <Skeleton variant='rounded' width='100%' height={132} />
                    <Skeleton variant='rounded' width='100%' height={132} />
                  </ScopeGrid>

                  <Skeleton variant='rounded' width='100%' height={154} />
                </RuleCard>
              </>
            ) : (
              <>
                <RulesHeader>
                  <div>
                    <SectionTitle>Document upload setup</SectionTitle>
                    <SectionHint>
                      Create one or more combinations for this document. In each
                      combination, choose the tags that identify the upload, then
                      decide whether one person handles the whole Building Passport or each
                      unit has its own assignee.
                    </SectionHint>
                    <ExampleHint>
                      Example: use one combination for Residential units and another
                      one for Commercial units if different people should upload the
                      same document.
                    </ExampleHint>
                  </div>
                  <PrimaryGhostButton type='button' onClick={handleAddRule}>
                    <AddOutlinedIcon fontSize='inherit' />
                    <span>Add Rules</span>
                  </PrimaryGhostButton>
                </RulesHeader>

                <RulesSummary>
                  <SummaryBadge>
                    {rules.length} combination{rules.length === 1 ? '' : 's'}
                  </SummaryBadge>
                  <SummaryBadge $active>
                    {tags?.length || 0} tags available
                  </SummaryBadge>
                  <SummaryBadge $active>
                    {rulesWithAssignments} with assignee
                    {rulesWithAssignments === 1 ? '' : 's'}
                  </SummaryBadge>
                </RulesSummary>

                {rules.length === 0 && (
                  <EmptyRulesState>
                    No combinations configured yet. Click{' '}
                    <strong>Add Combination</strong> to choose the tags and assign
                    the person responsible for this upload.
                  </EmptyRulesState>
                )}

                {rules.map((rule, index) => (
                  <RuleCard key={rule.key}>
                <RuleHeader>
                  <RuleTitleGroup>
                    <RuleIndex>{index + 1}</RuleIndex>
                    <RuleTitleContent>
                      <RuleName>
                        {rule.ruleName || buildGeneratedRuleName(item, index)}
                      </RuleName>
                      <RuleSubtitle>
                        {rule.tagIds.length > 0
                          ? `${rule.tagIds.length} tag${rule.tagIds.length === 1 ? '' : 's'} selected`
                          : 'Choose the tags that define when this combination should be used'}
                      </RuleSubtitle>
                    </RuleTitleContent>
                  </RuleTitleGroup>
                  <RuleActions>
                    <AttachTagsToUploadingFile
                      savedCount={rule.tagIds.length}
                      initiallySelected={rule.tagIds}
                      onSave={tagIds => updateRule(rule.key, { tagIds })}
                    >
                      <TagChooserButton type='button'>
                        <LocalOfferOutlinedIcon fontSize='inherit' />
                        <span>
                          {rule.tagIds.length > 0 ? 'Edit tags' : 'Choose tags'}
                        </span>
                        {rule.tagIds.length > 0 && (
                          <TagCountBadge>{rule.tagIds.length}</TagCountBadge>
                        )}
                      </TagChooserButton>
                    </AttachTagsToUploadingFile>
                    <DeleteRuleButton
                      type='button'
                      onClick={() => handleRemoveRule(rule.key)}
                    >
                      <DeleteOutlineOutlinedIcon fontSize='inherit' />
                      <span>Remove</span>
                    </DeleteRuleButton>
                  </RuleActions>
                </RuleHeader>

                <ScopeSectionLabel>
                  Who uploads this document?
                </ScopeSectionLabel>
                <ScopeGrid>
                  <ScopeOption
                    type='button'
                    $active={rule.assignmentScope === 'bp'}
                    onClick={() => handleScopeChange(rule.key, 'bp')}
                  >
                    <ScopeLabel>{bpName || 'Building Passport'}</ScopeLabel>
                    <ScopeText>
                      Assign one or more people to upload this document once for the
                      whole Building Passport.
                    </ScopeText>
                  </ScopeOption>
                  <ScopeOption
                    type='button'
                    $active={rule.assignmentScope === 'unit'}
                    onClick={() => handleScopeChange(rule.key, 'unit')}
                  >
                    <ScopeLabel>
                      {bpName
                        ? `Units from ${bpName}`
                        : 'Units from Building Passport'}
                    </ScopeLabel>
                    <ScopeText>
                      Assign the upload separately for each unit, such as each
                      apartment or floor.
                    </ScopeText>
                  </ScopeOption>
                </ScopeGrid>

                {rule.assignmentScope === 'bp' ? (
                  <AssigneePanel>
                    <AssigneeLabel>Assigned people</AssigneeLabel>
                    <UserPicker
                      value={rule.assigneeUserIds}
                      onChange={nextIds =>
                        updateRule(rule.key, {
                          assigneeUserIds: nextIds
                        })
                      }
                      buildingPassportId={bpId}
                      placeholder='Search Building Passport users...'
                      initialUsers={[]}
                      knownUsers={knownUsers}
                    />
                  </AssigneePanel>
                ) : (
                  <UnitsPanel>
                    <UnitsHeader>
                      <UnitsHeaderLeft>
                        <AssigneeLabel>Assign people by unit</AssigneeLabel>
                        <BulkAssignSection>
                          <BulkAssignHeader>
                            <div>
                              <BulkAssignLabel>
                                Bulk assign units
                              </BulkAssignLabel>
                              <BulkAssignHint>
                                Open a separate picker, select the exact units
                                you want, then assign one or more people to them together.
                              </BulkAssignHint>
                            </div>
                            <BulkActionButton
                              type='button'
                              onClick={() => openBulkAssignModal(rule)}
                            >
                              Select units
                            </BulkActionButton>
                          </BulkAssignHeader>
                        </BulkAssignSection>
                        <UnitsSummaryGrid>
                          <UnitsSummaryCard>
                            <UnitsSummaryValue>
                              {
                                rule.unitAssignments.filter(unitAssignment =>
                                  unitAssignment.assigneeUserIds.length > 0
                                ).length
                              }
                            </UnitsSummaryValue>
                            <UnitsSummaryLabel>Units assigned</UnitsSummaryLabel>
                          </UnitsSummaryCard>
                          <UnitsSummaryCard>
                            <UnitsSummaryValue>
                              {
                                rule.unitAssignments.filter(
                                  unitAssignment =>
                                    unitAssignment.assigneeUserIds.length === 0
                                ).length
                              }
                            </UnitsSummaryValue>
                            <UnitsSummaryLabel>Units unassigned</UnitsSummaryLabel>
                          </UnitsSummaryCard>
                          <UnitsSummaryCard>
                            <UnitsSummaryValue>
                              {rule.unitAssignments.length}
                            </UnitsSummaryValue>
                            <UnitsSummaryLabel>Total units</UnitsSummaryLabel>
                          </UnitsSummaryCard>
                        </UnitsSummaryGrid>
                        <UnitsPanelHint>
                          Individual assignment from this list is disabled. Use
                          the bulk picker to select units and assign one or more people to
                          them together.
                        </UnitsPanelHint>
                      </UnitsHeaderLeft>
                      {loadingUnits && <UnitsMeta>Loading units...</UnitsMeta>}
                    </UnitsHeader>
                    <UnitsAssignedPreview>
                      {rule.unitAssignments.some(unitAssignment =>
                        unitAssignment.assigneeUserIds.length > 0
                      ) ? (
                        rule.unitAssignments
                          .filter(unitAssignment =>
                            unitAssignment.assigneeUserIds.length > 0
                          )
                          .slice(0, 8)
                          .map(unitAssignment => (
                            <AssignedUnitChip
                              key={`${rule.key}-${unitAssignment.buildingUnitPassportId}`}
                            >
                              <span>{unitAssignment.name || 'Unit'}</span>
                              <strong>
                                {(() => {
                                  const user = knownUsers.find(
                                    u =>
                                      normalizeId(u.id) ===
                                      normalizeId(unitAssignment.assigneeUserIds[0])
                                  )
                                  const names = unitAssignment.assigneeUserIds
                                    .map(id =>
                                      knownUsers.find(u => normalizeId(u.id) === normalizeId(id))
                                    )
                                    .filter(Boolean)
                                    .map(user =>
                                      [user.first_name || user.firstName, user.last_name || user.lastName]
                                        .filter(Boolean)
                                        .join(' ') || user.email
                                    )

                                  if (names.length === 0) return 'Assigned'
                                  if (names.length === 1) return names[0]
                                  return `${names[0]} +${names.length - 1} more`
                                })()}
                              </strong>
                            </AssignedUnitChip>
                          ))
                      ) : (
                        <PanelMessage>
                          No units assigned yet. Use Select units to assign
                          users in bulk.
                        </PanelMessage>
                      )}
                    </UnitsAssignedPreview>
                  </UnitsPanel>
                )}
                  </RuleCard>
                ))}
              </>
            )}
          </RulesSection>

          <Actions>
            <SecondaryButton
              type='button'
              onClick={closeModal}
              disabled={saving}
            >
              Cancel
            </SecondaryButton>
            {hasTaskManagement && (
              <PrimaryOutlineButton
                type='button'
                onClick={() => handleSave(true)}
                disabled={saving || loadingUnits}
              >
                {saving && saveMode === 'create'
                  ? 'Creating tasks...'
                  : 'Save and create tasks'}
              </PrimaryOutlineButton>
            )}
            <PrimaryButton
              onClick={() => handleSave(false)}
              disabled={saving || loadingUnits}
            >
              {saving && saveMode === 'save' ? 'Saving...' : 'Save'}
            </PrimaryButton>
          </Actions>

          <Hint>
            <LocalOfferOutlinedIcon fontSize='inherit' />
            <span>
              {hasTaskManagement
                ? 'Save stores the combinations only. Save and create tasks also creates tasks for the assigned people.'
                : 'Save stores the combinations only. Task creation is available when task management is enabled for this Building Passport.'}
            </span>
          </Hint>
        </Content>
      </Modal>
      <Modal
        open={bulkModal.open}
        title='Bulk assign units'
        onClose={closeBulkAssignModal}
        minWidthDesktop='760px'
        maxWidthDesktop='860px'
      >
        <BulkModalContent>
          <BulkModalIntro>
            Select the units that should share the same assignees, then apply
            one or more people to all selected units in this combination.
          </BulkModalIntro>

          <BulkSelectionToolbar>
            <BulkSearchInput
              type='text'
              value={bulkModal.search}
              onChange={e =>
                setBulkModal(prev => ({
                  ...prev,
                  search: e.target.value
                }))
              }
              placeholder='Search units by name...'
            />
            <BulkToolbarButton
              type='button'
              onClick={handleSelectAllVisibleBulkUnits}
              disabled={filteredBulkUnitAssignments.length === 0}
            >
              Select all visible
            </BulkToolbarButton>
            <BulkToolbarButton
              type='button'
              onClick={handleClearBulkSelection}
              disabled={bulkModal.selectedUnitIds.length === 0}
            >
              Clear selection
            </BulkToolbarButton>
          </BulkSelectionToolbar>

          <BulkSelectionMeta>
            {bulkModal.selectedUnitIds.length} unit
            {bulkModal.selectedUnitIds.length === 1 ? '' : 's'} selected
            {filteredBulkUnitAssignments.length > 0 &&
              ` • ${filteredBulkUnitAssignments.length} visible`}
          </BulkSelectionMeta>

          <BulkUnitsList>
            {filteredBulkUnitAssignments.length > 0 ? (
              filteredBulkUnitAssignments.map(unitAssignment => {
                const unitId = unitAssignment.buildingUnitPassportId
                const isSelected = bulkModal.selectedUnitIds.includes(unitId)

                return (
                  <BulkUnitRow
                    key={`bulk-${unitId}`}
                    type='button'
                    $active={isSelected}
                    onClick={() => toggleBulkUnitSelection(unitId)}
                  >
                    <BulkUnitCheckbox
                      type='checkbox'
                      checked={isSelected}
                      onChange={() => toggleBulkUnitSelection(unitId)}
                      onClick={e => e.stopPropagation()}
                    />
                    <BulkUnitCopy>
                      <BulkUnitName>
                        {unitAssignment.name || 'Unit'}
                      </BulkUnitName>
                      <BulkUnitCurrentAssignee>
                        {unitAssignment.assigneeUserIds.length > 0
                          ? 'Already assigned'
                          : 'No assignees yet'}
                      </BulkUnitCurrentAssignee>
                    </BulkUnitCopy>
                  </BulkUnitRow>
                )
              })
            ) : (
              <PanelMessage>No units match this search.</PanelMessage>
            )}
          </BulkUnitsList>

          <BulkUserSection>
            <AssigneeLabel>Assign selected units to</AssigneeLabel>
            <UserPicker
              value={bulkModal.assigneeUserIds}
              onChange={nextIds =>
                setBulkModal(prev => ({
                  ...prev,
                  assigneeUserIds: nextIds
                }))
              }
              buildingPassportId={bpId}
              placeholder='Search Building Passport users...'
              initialUsers={[]}
              knownUsers={knownUsers}
            />
          </BulkUserSection>

          <BulkModalActions>
            <SecondaryButton type='button' onClick={closeBulkAssignModal}>
              Cancel
            </SecondaryButton>
            <BulkSecondaryButton
              type='button'
              onClick={handleClearBulkAssignedUnits}
              disabled={bulkModal.selectedUnitIds.length === 0}
            >
              Clear selected units
            </BulkSecondaryButton>
            <PrimaryButton
              onClick={handleApplyBulkSelection}
              disabled={
                bulkModal.assigneeUserIds.length === 0 ||
                bulkModal.selectedUnitIds.length === 0
              }
            >
              Assign selected units
            </PrimaryButton>
          </BulkModalActions>
        </BulkModalContent>
      </Modal>
    </>
  )
}

export default AssignChecklistItemModal

const TriggerWrapper = styled.button`
  border: 0;
  background: transparent;
  color: ${({ theme }) => theme.colors.haiti};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  padding: 2px 0;

  span {
    white-space: nowrap;
  }
`

const TriggerLabel = styled.span`
  line-height: 1;
`

const Content = styled.div`
  min-width: 0;
  width: min(880px, 100%);
  padding-bottom: 8px;

  @media (max-width: 768px) {
    width: 100%;
  }
`

const ItemCard = styled.div`
  display: flex;
  gap: 12px;
  padding: 16px;
  margin-bottom: 20px;
  border: 1px solid ${({ theme }) => theme.colors.grey[500]};
  background: ${({ theme }) => theme.colors.grey[300]};
`

const ItemIconWrap = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 38px;
  background: ${({ theme }) => theme.colors.grey[400]};
  color: ${({ theme }) => theme.colors.secondary};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
`

const ItemTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
`

const ItemDescription = styled.div`
  margin-top: 4px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey[700]};
`

const RulesSection = styled.div`
  margin-top: 4px;
`

const RulesHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`

const SectionTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
`

const SectionHint = styled.div`
  margin-top: 4px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey[700]};
  max-width: 620px;
  line-height: 1.5;
`

const ExampleHint = styled.div`
  margin-top: 8px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey[700]};
  max-width: 620px;
  line-height: 1.5;
  padding: 8px 10px;
  background: ${({ theme }) => theme.colors.grey[300]};
  border-left: 3px solid ${({ theme }) => theme.colors.secondary};
`

const PrimaryGhostButton = styled.button`
  border: 1px solid ${({ theme }) => theme.colors.secondary};
  background: #fff;
  color: ${({ theme }) => theme.colors.secondary};
  min-height: 40px;
  padding: 0 16px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-weight: 700;
  white-space: nowrap;
  flex-shrink: 0;
`

const TagChooserButton = styled.button`
  border: 1px solid ${({ theme }) => theme.colors.grey[500]};
  background: #fff;
  color: ${({ theme }) => theme.colors.haiti};
  min-height: 38px;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-weight: 700;
`

const TagCountBadge = styled.span`
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  border-radius: 22px;
  background: ${({ theme }) => theme.colors.secondary};
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
`

const RulesSummary = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 14px;
`

const SummaryBadge = styled.div`
  height: 28px;
  border-radius: 20px;
  padding: 0 12px;
  border: 1px solid
    ${({ theme, $active }) =>
      $active ? theme.colors.secondary : theme.colors.grey[500]};
  background: ${({ theme, $active }) =>
    $active ? '#fff' : theme.colors.grey[300]};
  color: ${({ theme, $active }) =>
    $active ? theme.colors.secondary : theme.colors.haiti};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
`

const RuleCard = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.grey[500]};
  background: #fff;
  padding: 16px;

  &:not(:last-child) {
    margin-bottom: 12px;
  }
`

const EmptyRulesState = styled.div`
  border: 1px dashed ${({ theme }) => theme.colors.grey[500]};
  background: ${({ theme }) => theme.colors.grey[300]};
  padding: 16px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey[700]};
  line-height: 1.5;
  margin-bottom: 12px;
`

const RuleHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`

const RuleTitleGroup = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`

const RuleTitleContent = styled.div`
  min-width: 0;
`

const RuleIndex = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 30px;
  background: ${({ theme }) => theme.colors.grey[300]};
  color: ${({ theme }) => theme.colors.haiti};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  flex-shrink: 0;
`

const RuleName = styled.div`
  color: ${({ theme }) => theme.colors.haiti};
  font-size: 14px;
  font-weight: 700;
`

const RuleSubtitle = styled.div`
  margin-top: 4px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey[700]};
`

const RuleActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: space-between;
  }
`

const DeleteRuleButton = styled.button`
  border: 1px solid ${({ theme }) => theme.colors.grey[500]};
  background: #fff;
  color: ${({ theme }) => theme.colors.haiti};
  min-height: 38px;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
`

const ScopeSectionLabel = styled.div`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[700]};
  margin-bottom: 8px;
`

const ScopeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const ScopeOption = styled.button`
  border: 1px solid
    ${({ theme, $active }) =>
      $active ? theme.colors.secondary : theme.colors.grey[500]};
  background: ${({ $active }) => ($active ? '#fff' : '#fafafa')};
  padding: 14px;
  text-align: left;
  cursor: pointer;
`

const ScopeLabel = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
`

const ScopeText = styled.div`
  margin-top: 6px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey[700]};
  line-height: 1.45;
`

const AssigneePanel = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.grey[500]};
  background: ${({ theme }) => theme.colors.grey[300]};
  padding: 14px;
`

const AssigneeLabel = styled.div`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[700]};
  margin-bottom: 10px;
`

const UnitsPanel = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.grey[500]};
  background: ${({ theme }) => theme.colors.grey[300]};
  padding: 14px;
`

const UnitsHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
`

const UnitsHeaderLeft = styled.div`
  flex: 1;
  min-width: 0;
`

const UnitsMeta = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey[700]};
`

const UnitsSummaryGrid = styled.div`
  margin-top: 14px;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const UnitsSummaryCard = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.grey[500]};
  background: #fff;
  padding: 12px;
`

const UnitsSummaryValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
`

const UnitsSummaryLabel = styled.div`
  margin-top: 4px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey[700]};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`

const UnitsPanelHint = styled.div`
  margin-top: 12px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey[700]};
  line-height: 1.5;
`

const BulkAssignSection = styled.div`
  margin-top: 12px;
  margin-bottom: 12px;
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.colors.grey[500]};
  background: #fff;
`

const BulkAssignHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`

const BulkAssignLabel = styled.div`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[700]};
`

const BulkAssignHint = styled.div`
  margin-top: 6px;
  margin-bottom: 10px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey[700]};
  line-height: 1.45;
`

const BulkActionButton = styled.button`
  border: 1px solid ${({ theme }) => theme.colors.secondary};
  background: ${({ theme }) => theme.colors.secondary};
  color: #fff;
  min-height: 40px;
  padding: 0 14px;
  cursor: pointer;
  font-weight: 700;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const BulkSecondaryButton = styled.button`
  border: 1px solid ${({ theme }) => theme.colors.grey[500]};
  background: #fff;
  color: ${({ theme }) => theme.colors.haiti};
  min-height: 40px;
  padding: 0 14px;
  cursor: pointer;
  font-weight: 700;
`

const BulkModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`

const BulkModalIntro = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey[700]};
  line-height: 1.5;
`

const BulkSelectionToolbar = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  gap: 10px;
  align-items: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const BulkSearchInput = styled.input`
  width: 100%;
  height: 40px;
  padding: 0 12px;
  border: 1px solid ${({ theme }) => theme.colors.grey[500]};
  background: #fff;
  color: ${({ theme }) => theme.colors.haiti};
`

const BulkToolbarButton = styled.button`
  border: 1px solid ${({ theme }) => theme.colors.grey[500]};
  background: #fff;
  color: ${({ theme }) => theme.colors.haiti};
  min-height: 40px;
  padding: 0 14px;
  cursor: pointer;
  font-weight: 700;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const BulkSelectionMeta = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey[700]};
`

const BulkUnitsList = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.grey[500]};
  background: ${({ theme }) => theme.colors.grey[300]};
  max-height: 420px;
  overflow-y: auto;
`

const BulkUnitRow = styled.button`
  width: 100%;
  border: 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.grey[500]};
  background: ${({ $active }) => ($active ? '#f5fdff' : '#fff')};
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  text-align: left;
  cursor: pointer;

  &:last-child {
    border-bottom: 0;
  }
`

const BulkUnitCheckbox = styled.input`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
`

const BulkUnitCopy = styled.div`
  min-width: 0;
`

const BulkUnitName = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
`

const BulkUnitCurrentAssignee = styled.div`
  margin-top: 4px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey[700]};
`

const BulkUserSection = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.grey[500]};
  background: ${({ theme }) => theme.colors.grey[300]};
  padding: 14px;
`

const BulkModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  flex-wrap: wrap;
`

const UnitsAssignedPreview = styled.div`
  margin-top: 18px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`

const AssignedUnitChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.colors.grey[500]};
  background: #fff;
  color: ${({ theme }) => theme.colors.haiti};
  font-size: 12px;

  span {
    margin-right: 0;
  }

  strong {
    font-weight: 700;
  }
`

const PanelMessage = styled.div`
  padding: 16px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey[700]};
  line-height: 1.5;
`

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
  flex-wrap: wrap;
`

const SecondaryButton = styled.button`
  border: 1px solid ${({ theme }) => theme.colors.grey[600]};
  background: #fff;
  color: ${({ theme }) => theme.colors.haiti};
  min-width: 96px;
  height: 44px;
  cursor: pointer;
`

const PrimaryOutlineButton = styled.button`
  border: 1px solid ${({ theme }) => theme.colors.secondary};
  background: #fff;
  color: ${({ theme }) => theme.colors.secondary};
  min-width: 168px;
  height: 44px;
  cursor: pointer;
  font-weight: 700;
`

const Hint = styled.div`
  margin-top: 14px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey[700]};
  line-height: 1.45;
`
